import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import {
    Action,
    createFeatureSelector,
    createReducer,
    createSelector,
    on
} from '@ngrx/store';
import * as fromShared from '../../shared';
import * as CompanySettingActions from './company-setting.actions';
import { CompanySetting } from './company-setting.model';

export const companySettingsFeatureKey = 'companySettings';

export type State = EntityState<CompanySetting>;
// have to specify function to get the uniqueid column since we're not using "id"
export const adapter: EntityAdapter<CompanySetting> =
    createEntityAdapter<CompanySetting>({
        selectId: (model: CompanySetting) => model.SettingType
    });

export const initialState: State = adapter.getInitialState({
    // additional entity state properties
});

const companySettingReducer = createReducer(
    initialState,
    on(CompanySettingActions.addCompanySetting, (state, action) =>
        adapter.addOne(action.companySetting, state)
    ),
    on(CompanySettingActions.upsertCompanySetting, (state, action) =>
        adapter.upsertOne(action.companySetting, state)
    ),
    on(CompanySettingActions.addCompanySettings, (state, action) =>
        adapter.addMany(action.companySettings, state)
    ),
    on(CompanySettingActions.upsertCompanySettings, (state, action) =>
        adapter.upsertMany(action.companySettings, state)
    ),
    on(CompanySettingActions.updateCompanySetting, (state, action) =>
        adapter.updateOne(action.companySetting, state)
    ),
    on(CompanySettingActions.updateCompanySettings, (state, action) =>
        adapter.updateMany(action.companySettings, state)
    ),
    on(CompanySettingActions.deleteCompanySetting, (state, action) =>
        adapter.removeOne(action.id, state)
    ),
    on(CompanySettingActions.deleteCompanySettings, (state, action) =>
        adapter.removeMany(action.ids, state)
    ),
    on(CompanySettingActions.loadCompanySettings, (state, action) =>
        adapter.setAll(action.companySettings, state)
    ),
    on(CompanySettingActions.clearCompanySettings, state =>
        adapter.removeAll(state)
    )
);

export function reducer(state: State | undefined, action: Action) {
    return companySettingReducer(state, action);
}
export const localState = createFeatureSelector<State>(
    companySettingsFeatureKey
);

export const { selectIds, selectEntities, selectAll, selectTotal } =
    adapter.getSelectors(localState);
export const getCategories = createSelector(selectAll, settings =>
    fromShared.uniqBy(
        settings.map(x => ({
            CatID: x.CatID,
            Description: x.CatDescription
        })),
        x => x.CatID
    )
);
export const selectEntity = id =>
    createSelector(selectEntities, entities => entities[id]);
export const getReminderStatus = createSelector(selectAll, settings =>
    settings.find(x => x.SettingType === 'RmdrStatus')
);

// export const getSettingValue = createSelector(
//     selectAll,
//     (settings: CompanySetting[], props: { SettingType: string }) =>
//         settings.find(x => x.SettingType === props.SettingType)
//             ? settings.find(x => x.SettingType === props.SettingType)
//                   ?.SettingValue
//             : null
// );
export const getSettingValue = (settingType: string) =>
    createSelector(
        selectAll,
        settings =>
            settings.find(x => x.SettingType === settingType)
                ?.SettingValue as string
    );

export const getSetting = (settingType: string) =>
    createSelector(
        selectAll,
        settings =>
            settings.find(x => x.SettingType === settingType) as CompanySetting
    );

export const getSettingBooleanValue = (settingType: string) =>
    createSelector(selectAll, (settings: CompanySetting[]) => {
        const value = settings.find(x => x.SettingType === settingType);
        return value
            ? value.SettingValue === '1' ||
                  value.SettingValue === 1 ||
                  value.SettingValue === 'true' ||
                  value.SettingValue === true
            : false;
    });

export const getSettingDateValue = (settingType: string) =>
    createSelector(selectAll, settings =>
        settings.find(x => x.SettingType === settingType)?.SettingValue
            ? new Date(
                  settings.find(
                      x => x.SettingType === settingType
                  )?.SettingValue
              )
            : undefined
    );

export enum CompanySettingEnum {
    QuickbooksOnline = 'QuickbooksOnline',
    QBOWebhooks = 'QBOWebhooks',
    SendEmployeetoQB = 'SendEmployeetoQB',
    SendDoctoQB = 'SendDoctoQB',
    ExternalEmployeeSource = 'ExternalEmployeeSource',
    GovConPayId = 'GovConPayId',
    GovConLegalId = 'GovConPayLegalId',
    GovConLegalCode = 'GovConPayLegalCode',
    ExternalCompanyId = 'ExternalCompanyId',
    TaxpayerID = 'TaxpayerID',
    CompanyName = 'CompanyName',
    EmployeeNameOrder = 'EmployeeNameOrder',
    ExternalEmployeeLastSync = 'ExternalEmployeeLastSync',
    ExternalEmployeeValidationLastRun = 'ExternalEmployeeValidationLastRun',
    ExternalEmployeeValidationComplete = 'ExternalEmployeeValidationComplete',
    ExpenseReimbursementPaycode = 'ExpenseReimbursementPaycode',
    ReimburseViaPayroll = 'reimburseviapayroll',
    ShowAIHelpButton = 'ShowAIHelpButton'
}

export const isGCPEnabled = getSettingBooleanValue(CompanySettingEnum.ExternalEmployeeSource);
export const isReimburseViaPayrollEnabled = getSettingBooleanValue(CompanySettingEnum.ReimburseViaPayroll);
export const isQBOEnabled = getSettingBooleanValue(CompanySettingEnum.QuickbooksOnline);
export const isQBOWebhooksEnabled = getSettingBooleanValue(CompanySettingEnum.QBOWebhooks);
export const SendEmployeetoQB = getSettingBooleanValue(CompanySettingEnum.SendEmployeetoQB);
export const SendDoctoQB = getSettingBooleanValue(CompanySettingEnum.SendDoctoQB);
